import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getSearchData } from '../actions/aiSearch';

interface searchData {
  type: string | null;
  query: string | null;
  city: string | null;
}

interface SelectedRoom {
  _id: string;
  RoomName: string;
  RoomNumber: string;
  VideoCallPrice: number;
  AudioCallPrice: number;
  Description: string;
  categoryId: string;
  RoomStatus: string; // If RoomStatus has fixed values, you can use a union type
  CreateBy: string;
  Keywords: string[];
  createdAt: string; // Consider `Date` if you're working with actual date objects
  updatedAt: string;
  __v: number;
}
interface AiSearch {
  loading: boolean;
  error: string | null;
  data: searchData | null;
  country: any;
  isGlobal: boolean;
  rooms: any;
  screen: string;
  selectedRoom: SelectedRoom | null;
  roomLabel: any;
  typeOfService: any;
  city: string | null;
  address: string | null;
  balance: Number | undefined;
  radius: Number | undefined;
  queryLogId: string | null;
  coordinates: [] | null;
}

const initialState: AiSearch = {
  loading: false,
  error: null,
  rooms: null,
  data: {
    type: 'consult',
    query: null,
    city: null,
  },
  typeOfService: {
    serviceType: 'onsite',
    providerType: 'any',
  },
  isGlobal: false,
  country: null,
  city: null,
  screen: 'country',
  selectedRoom: null,
  roomLabel: null,
  balance: 0,
  queryLogId: null,
  address: null,
  coordinates: null,
  radius: 1,
};

const aiSearchSlice = createSlice({
  name: 'aiSearch',
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<any>) => {
      state.data = action.payload;
    },
    setRadiusInStore: (state, action: PayloadAction<any>) => {
      state.radius = action.payload;
    },
    setCoordinates: (state, action: PayloadAction<any>) => {
      state.coordinates = action.payload;
    },
    setBalance: (state, action: PayloadAction<any>) => {
      state.balance = action.payload;
    },
    setScreen: (state, action: PayloadAction<any>) => {
      state.screen = action.payload;
    },
    setSelectedRoom: (state, action: PayloadAction<any>) => {
      state.selectedRoom = action.payload;
    },
    setTypeOfService: (state, action: PayloadAction<any>) => {
      state.typeOfService = action.payload;
    },
    setCountry: (state, action: PayloadAction<any>) => {
      state.country = action.payload;
    },
    setCityName: (state, action: PayloadAction<any>) => {
      state.city = action.payload;
    },
    setIsGlobal: (state, action: PayloadAction<any>) => {
      state.isGlobal = action.payload;
    },
    setRoomLabel: (state, action: PayloadAction<any>) => {
      state.roomLabel = action.payload;
    },
    setQueryLogId: (state, action: PayloadAction<string>) => {
      state.queryLogId = action.payload;
    },
    setAddress: (state, action: PayloadAction<string>) => {
      state.address = action.payload;
    },
    clearAllData: (state) => {
      state.loading = false;
      state.error = null;
      state.rooms = null;
      (state.data = {
        type: 'consult',
        query: null,
        city: null,
      }),
        (state.typeOfService = {
          serviceType: 'onsite',
          providerType: 'any',
        }),
        (state.country = null);
      state.city = null;
      state.screen = 'country';
      state.selectedRoom = null;
      state.roomLabel = null;
      state.queryLogId = null;
      state.address = null;
    },
    // Define any synchronous reducers here, if needed
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSearchData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSearchData.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.rooms = action.payload.rooms; // Assuming 'rooms' is directly in the root
        state.queryLogId = action.payload.queryLogId;
        state.error = null;
        state.address = action.payload.address;
      })

      .addCase(getSearchData.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload; // Error handling with the rejected action payload
      });
  },
});

// Export the actions, if there are any
export const {
  setBalance,
  setRoomLabel,
  setIsGlobal,
  clearAllData,
  setData,
  setScreen,
  setSelectedRoom,
  setTypeOfService,
  setCountry,
  setCityName,
  setQueryLogId,
  setAddress,
  setCoordinates,
  setRadiusInStore,
} = aiSearchSlice.actions;

// Export the reducer
export default aiSearchSlice.reducer;
